import { useQuery } from "@tanstack/react-query";
import { apiService } from "../../services/apiService";
import { CursorTooltip } from "../CursorTooltip";
import { StatusCell } from "../molecules/StatusCell";
import { TextCell } from "../molecules/TextCell";
import { ResidentNameCell } from "../molecules/ResidentNameCell";
import { CompletionDate } from "../molecules/CompletionDate";
import { ResidentData } from "../../types/dashboard";
import { WorkItem } from "../../types/workItems";
import { useApiClient } from "../../contexts/ApiClientContext";
import { EllipsisVertical, Ban } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../ui/dropdown-menu";

export interface MDSRowClickData {
  row: ResidentData;
  fullWorkItem?: WorkItem;
}

interface MDSAssessmentRowProps {
  row: ResidentData;
  index: number;
  isLastRow: boolean;
  onClick: (data: MDSRowClickData) => void;
  onDismiss: (row: ResidentData) => void;
  canDismiss?: boolean;
  style?: React.CSSProperties;
}

export function MDSAssessmentRow({ row, index, isLastRow, onClick, onDismiss, canDismiss = false, style }: MDSAssessmentRowProps) {
  // Get API client from context (widget) or use default (main app)
  const apiClient = useApiClient();

  // Fetch full work item details if work_item_id is available
  const { data: fullWorkItem } = useQuery({
    queryKey: ["work-item", row.work_item_id],
    queryFn: async () => {
      // Use widget API client if available, otherwise use main app API
      if (apiClient) {
        return apiClient.getWorkItem(row.work_item_id!);
      } else {
        return apiService.getSingleWorkItem(row.work_item_id!);
      }
    },
    enabled: !!row.work_item_id,
    staleTime: 60 * 1000, // Match auto-refresh interval
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Map fields with fallbacks for MDS Assessment columns
  // Use fullWorkItem data if available, otherwise fallback to row data
  const referenceWindow = row.referenceWindow || "N/A";
  // unsigned_sections is a string from EHR (e.g., "Z" or "A, B, C")
  const unsignedSections = fullWorkItem?.unsigned_sections || row.assignedServices || "N/A";
  // MDS Completion date - API sets due_date to mds_completion_date if available
  const completionDate = fullWorkItem?.due_date || row.dueDate || "N/A";
  // Clinical dates - use row data (from table mapping) or fullWorkItem as fallback
  // Check both top-level flattened fields and nested clinical_dates for backward compatibility
  // Note: Must check for truthy value AND not "N/A" to properly fallback (undefined !== "N/A" is true!)
  const caaCompletion = (row.caaCompletion && row.caaCompletion !== "N/A")
    ? row.caaCompletion
    : (fullWorkItem?.caa_completion_date || fullWorkItem?.clinical_dates?.caa_completion_date || "N/A");
  const carePlanCompletionDate = (row.carePlanCompletionDate && row.carePlanCompletionDate !== "N/A")
    ? row.carePlanCompletionDate
    : (fullWorkItem?.care_plan_completion_date || fullWorkItem?.clinical_dates?.care_plan_completion_date || "N/A");
  const transmissionDate = (row.transmissionDate && row.transmissionDate !== "N/A")
    ? row.transmissionDate
    : (fullWorkItem?.transmission_date || fullWorkItem?.clinical_dates?.transmission_date || "N/A");

  // Apply italic styling for Queued status on date-related columns
  const isQueued = row.statusNormalized === "Queued";
  const showDismissMenu = isQueued && canDismiss;

  // NETSOL-12004: DOB, Payor, Location and Reference Window are no longer
  // columns — they surface in this row hover-over card instead.
  const hoverDetails = (
    <div className="min-w-[220px] space-y-1.5 text-sm">
      <p className="font-semibold text-popover-foreground">{row.residentName}</p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        <dt className="text-muted-foreground">Date of Birth</dt>
        <dd className="text-right">{row.dateOfBirth || "N/A"}</dd>
        <dt className="text-muted-foreground">Payor</dt>
        <dd className="text-right">{row.payor || "N/A"}</dd>
        <dt className="text-muted-foreground">Location</dt>
        <dd className="text-right">{row.location || "N/A"}</dd>
        <dt className="text-muted-foreground">Reference Window</dt>
        <dd className="text-right">{referenceWindow}</dd>
      </dl>
    </div>
  );

  return (
    <CursorTooltip
      key={`${row.residentName}-${index}`}
      content={hoverDetails}
      onClick={() => onClick({ row, fullWorkItem })}
      className={`border-b border-border cursor-pointer transition-colors hover:bg-muted/50 ${
        isLastRow ? "border-b-0" : ""
      }`}
      style={style}
    >
      {/* Action Menu - visible only when status is Queued and user has dismiss permission */}
      <td className="p-1 text-center" onClick={(e) => e.stopPropagation()}>
        {showDismissMenu && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-1 rounded hover:bg-muted">
                <EllipsisVertical className="h-4 w-4 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[180px]">
              <DropdownMenuItem
                onSelect={() => onDismiss(row)}
                className="text-destructive focus:text-destructive"
              >
                <Ban className="h-4 w-4 mr-2" />
                Dismiss
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </td>

      {/* MDS Assessment columns (NETSOL-12004: ARD first; DOB/Payor/Location/
          Reference Window moved to the row hover-over above). Cell order MUST
          match MDS_COLUMNS in MDSAssessmentsTable. */}
      <TextCell value={row.ardDate} muted italic={isQueued} />
      <StatusCell status={row.statusNormalized} statusBucket={row.statusBucket} />
      <ResidentNameCell residentName={row.residentName} />
      <TextCell value={row.admissionDate} muted />
      <TextCell value={row.mdsOcType} />
      <TextCell value={unsignedSections} muted />
      {/* statusBucket (raw `status_normalized`), NOT statusNormalized — the
          latter is the EHR-native display label from mdsAssessmentStatusLabel()
          and would never match CompletionDate's bucket check. */}
      <CompletionDate date={completionDate} status={row.statusBucket} italic={isQueued} />
      <TextCell value={caaCompletion} muted italic={isQueued} />
      <TextCell value={carePlanCompletionDate} muted italic={isQueued} />
      <TextCell value={transmissionDate} muted italic={isQueued} />
    </CursorTooltip>
  );
}
